import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import Big from 'bignumber.js';

import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import type { useAuctionCurrentPrice_auction } from './__generated__/auctions.graphql';

export function useAuctionCurrentPrice(
  auction: useAuctionCurrentPrice_auction
): MonetaryAmount;
export function useAuctionCurrentPrice(
  auction: useAuctionCurrentPrice_auction | null
): MonetaryAmount | null;
export function useAuctionCurrentPrice(
  auction: useAuctionCurrentPrice_auction | null
): MonetaryAmount | null {
  const { toMonetaryAmount, toMonetaryAmountInReferenceCurrency } =
    useMonetaryAmount();
  if (!auction) return null;
  const { currentPrice, privateCurrentPrice, currency, myBestBid } = auction;
  if (myBestBid) {
    const { amounts } = myBestBid;
    return toMonetaryAmount(amounts);
  }
  return toMonetaryAmountInReferenceCurrency(
    new Big(privateCurrentPrice).gt(currentPrice)
      ? privateCurrentPrice
      : currentPrice,
    currency
  );
}
useAuctionCurrentPrice.fragments = {
  auction: gql`
    fragment useAuctionCurrentPrice_auction on TokenAuction {
      id
      currentPrice
      privateCurrentPrice
      currency
      myBestBid {
        id
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useAuctionCurrentPrice_auction>,
};

export const auctionMinNextBid = (auction: {
  minNextBid: string;
  privateMinNextBid: string;
}) => {
  const { minNextBid, privateMinNextBid } = auction;
  return new Big(privateMinNextBid).gt(minNextBid)
    ? privateMinNextBid
    : minNextBid;
};
